import React, { useEffect, useMemo, useRef, useState } from "react";
import { LogIn, LogOut, Plus, FileText, Send, CheckCircle2, XCircle, Share2, ArrowLeft, ShieldCheck, PenLine, BarChart3, Database, Users, MapPin, Building2, HelpCircle, Sparkles, Image, Network, Globe, Clock, Loader2, Inbox, AlertTriangle, Download } from "lucide-react";
import { toast } from "sonner";
import { useAuth, login } from "@/context/AuthContext";
import {
  cmsList, cmsGet, cmsCreate, cmsUpdate, cmsSubmit, cmsPublish, cmsReject, getNavTaxonomy, getContentStats, getSSOSites, setSSOStatus, getModerationQueue, moderateContribution, getArchiveRecovery, getArchiveRecords, API,
} from "@/lib/api";
import { RichEditor } from "@/components/site/RichEditor";
import { CommunityModeration } from "@/components/admin/CommunityModeration";
import { useLanguage } from "@/context/LanguageContext";
import { copyText } from "@/lib/clipboard";

const STATUS_STYLE = {
  draft: "bg-slate-100 text-slate-600",
  in_review: "bg-amber-100 text-amber-700",
  published: "bg-emerald-100 text-emerald-700",
  archived: "bg-slate-100 text-slate-400",
};

export default function AdminCMS() {
  const { user, loading, logout, refresh, signInWithSocialPulse } = useAuth();
  const [spBusy, setSpBusy] = useState(false);

  const handleSocialPulse = async () => {
    setSpBusy(true);
    try {
      await signInWithSocialPulse();
      toast.success("Signed in with SocialPulse");
    } catch (e) {
      const code = e?.code || "";
      if (code === "auth/popup-closed-by-user" || code === "auth/cancelled-popup-request") {
        // user dismissed — no error toast
      } else if (code === "auth/unauthorized-domain") {
        toast.error("This domain isn't authorized in Firebase yet. Ask the SocialPulse owner to add it.");
      } else {
        toast.error(e?.response?.data?.detail || "SocialPulse sign-in failed");
      }
    } finally { setSpBusy(false); }
  };

  // If we land on /admin with a session cookie but the initial auth check raced
  // (fired before the cookie was live), re-verify with a few short retries.
  useEffect(() => {
    if (loading || user) return;
    let stop = false;
    let tries = 0;
    const attempt = async () => {
      if (stop) return;
      const u = await refresh();
      if (!u && !stop && tries < 5) { tries += 1; setTimeout(attempt, 700); }
    };
    attempt();
    return () => { stop = true; };
  }, [loading, user, refresh]);

  if (loading) return <Center><p className="animate-pulse font-display text-xl text-navy">Loading…</p></Center>;

  if (!user) return (
    <Center>
      <div className="max-w-md rounded-2xl border border-border bg-white p-8 text-center shadow-sm" data-testid="cms-login">
        <ShieldCheck className="mx-auto mb-4 h-10 w-10 text-oldglory-red" />
        <h1 className="font-display text-2xl font-black text-navy">USAPulse CMS</h1>
        <p className="mt-2 text-sm text-muted-foreground">Sign in to write, review and publish place articles across the network.</p>
        <button onClick={handleSocialPulse} disabled={spBusy} className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-oldglory-red px-6 py-3 font-semibold text-white transition-colors hover:bg-oldglory-dark disabled:opacity-60" data-testid="cms-socialpulse-btn">
          {spBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Network className="h-4 w-4" />} Continue with SocialPulse
        </button>
        <button onClick={login} className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full border border-navy/20 bg-white px-6 py-3 font-semibold text-navy transition-colors hover:bg-parchment" data-testid="cms-login-btn">
          <LogIn className="h-4 w-4" /> Sign in with Google
        </button>
        <p className="mt-3 text-[11px] text-muted-foreground">One SocialPulse account works across all sister sites.</p>
      </div>
    </Center>
  );

  return <Dashboard user={user} logout={logout} />;
}

function Dashboard({ user, logout }) {
  const [editingId, setEditingId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [showProgress, setShowProgress] = useState(false);
  const [showSso, setShowSso] = useState(false);
  const [showMod, setShowMod] = useState(false);
  const [showArchive, setShowArchive] = useState(false);
  const [showCommunity, setShowCommunity] = useState(false);

  if (showProgress) return <ProgressDashboard onBack={() => setShowProgress(false)} />;
  if (showSso) return <SsoStatusBoard onBack={() => setShowSso(false)} />;
  if (showMod) return <ModerationPanel onBack={() => setShowMod(false)} />;
  if (showCommunity) return <CommunityModeration onBack={() => setShowCommunity(false)} />;
  if (showArchive) return <ArchiveRecoveryPanel onBack={() => setShowArchive(false)} />;
  if (editingId) return <Editor id={editingId} user={user} onBack={() => setEditingId(null)} />;
  if (creating) return <CreateNew user={user} onCreated={(id) => { setCreating(false); setEditingId(id); }} onBack={() => setCreating(false)} />;
  return <ArticleList user={user} logout={logout} onEdit={setEditingId} onNew={() => setCreating(true)} onProgress={() => setShowProgress(true)} onSso={() => setShowSso(true)} onModeration={() => setShowMod(true)} onArchive={() => setShowArchive(true)} onCommunity={() => setShowCommunity(true)} />;
}

const KIND_LABEL = {
  story: "Local story", correction: "Correction", event: "Event",
  organization: "Organization", photo: "Photo", accessibility: "Issue report",
};

function ModerationPanel({ onBack }) {
  const [tab, setTab] = useState("pending");
  const [data, setData] = useState({ contributions: [], counts: {} });
  const [busy, setBusy] = useState(null);

  const load = (status) => getModerationQueue(status).then(setData).catch((e) => toast.error(e?.response?.data?.detail || "Load failed"));
  useEffect(() => { load(tab); }, [tab]);

  const act = async (cid, action) => {
    setBusy(cid);
    try {
      await moderateContribution(cid, action);
      toast.success(action === "approve" ? "Approved & published" : "Rejected");
      load(tab);
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    finally { setBusy(null); }
  };

  const counts = data.counts || {};
  return (
    <div className="mx-auto max-w-4xl px-4 py-8" data-testid="moderation-panel">
      <button onClick={onBack} className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-navy hover:text-oldglory-red" data-testid="mod-back"><ArrowLeft className="h-4 w-4" /> Back</button>
      <h1 className="mb-1 font-display text-3xl font-black text-navy">Contribution Moderation</h1>
      <p className="mb-5 text-sm text-muted-foreground">Nothing publishes without review. High-risk items are flagged.</p>

      <div className="mb-5 flex gap-2">
        {["pending", "approved", "rejected"].map((s) => (
          <button key={s} onClick={() => setTab(s)}
            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-semibold capitalize transition-colors ${tab === s ? "bg-navy text-white" : "border border-border bg-white text-navy hover:bg-parchment"}`}
            data-testid={`mod-tab-${s}`}>
            {s} <span className="rounded-full bg-black/10 px-1.5 text-[11px]">{counts[s] ?? 0}</span>
          </button>
        ))}
      </div>

      {data.contributions.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-12 text-center text-muted-foreground" data-testid="mod-empty">No {tab} submissions.</div>
      ) : (
        <div className="space-y-3" data-testid="mod-list">
          {data.contributions.map((c) => (
            <div key={c.id} className="rounded-xl border border-border bg-white p-4" data-testid={`mod-item-${c.id}`}>
              <div className="mb-1.5 flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-navy/5 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-navy">{KIND_LABEL[c.kind] || c.kind}</span>
                {c.high_risk && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-bold text-red-700 ring-1 ring-red-500/30" data-testid={`mod-highrisk-${c.id}`}>
                    <AlertTriangle className="h-3 w-3" /> High-risk — extra review
                  </span>
                )}
                <span className="text-xs text-muted-foreground">{c.place_name}</span>
              </div>
              <h3 className="font-display text-lg font-bold text-navy">{c.title}</h3>
              <p className="mt-1 whitespace-pre-line text-sm text-ink/80">{c.content}</p>
              <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                <span>By {c.author_name}{c.author_role ? ` · ${c.author_role}` : ""}</span>
                {c.source_url && <a href={c.source_url} target="_blank" rel="noopener noreferrer" className="font-semibold text-oldglory-red hover:underline">Source ↗</a>}
                {c.event_date && <span>Event: {c.event_date}</span>}
              </div>
              {tab === "pending" && (
                <div className="mt-3 flex gap-2">
                  <button onClick={() => act(c.id, "approve")} disabled={busy === c.id}
                    className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60" data-testid={`mod-approve-${c.id}`}>
                    <CheckCircle2 className="h-4 w-4" /> Approve
                  </button>
                  <button onClick={() => act(c.id, "reject")} disabled={busy === c.id}
                    className="inline-flex items-center gap-1.5 rounded-full border border-oldglory-red/30 px-4 py-1.5 text-sm font-semibold text-oldglory-red hover:bg-red-50 disabled:opacity-60" data-testid={`mod-reject-${c.id}`}>
                    <XCircle className="h-4 w-4" /> Reject
                  </button>
                </div>
              )}
              {c.reviewed_by && <p className="mt-2 text-[11px] text-muted-foreground">Reviewed by {c.reviewed_by}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const RECOVERY_CLASS = {
  A: "Structure to adopt", B: "Fact to reverify", C: "Historical fact",
  D: "Public-domain source lead", E: "Editorial idea", F: "Obsolete", G: "Do not migrate",
};

function ArchiveRecoveryPanel({ onBack }) {
  const [data, setData] = useState(null);
  const [view, setView] = useState("overview"); // overview | records
  const [records, setRecords] = useState(null);
  const [kind, setKind] = useState("all");
  useEffect(() => { getArchiveRecovery().then(setData).catch(() => {}); }, []);
  useEffect(() => {
    if (view === "records") {
      setRecords(null);
      getArchiveRecords(kind === "all" ? undefined : kind).then(setRecords).catch(() => {});
    }
  }, [view, kind]);

  const exportCsv = async () => {
    const all = await getArchiveRecords();
    const cols = ["record_id", "kind", "region_key", "legacy_title", "legacy_section",
      "entity_type", "state_slug", "city_slug", "usapulse_tab", "usapulse_module",
      "copyright_class", "migration_status", "modern_route", "source_url", "notes"];
    const esc = (v) => {
      const s = v === undefined || v === null ? "" : Array.isArray(v) ? v.join("|") : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = [cols.join(",")].concat(all.records.map((r) => cols.map((c) => esc(r[c])).join(",")));
    const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `usapulse-geography-recovery-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="mx-auto max-w-5xl px-4 py-8" data-testid="archive-recovery-panel">
      <button onClick={onBack} className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-navy hover:text-oldglory-red" data-testid="archive-back"><ArrowLeft className="h-4 w-4" /> Back</button>
      <h1 className="mb-1 font-display text-3xl font-black text-navy">Legacy Geography Recovery</h1>
      <p className="mb-4 text-sm text-muted-foreground">
        Reusable information-architecture recovered from the legacy state portal ({data?.summary?.snapshot_date || "2012"}). Structure &amp; taxonomy only — no legacy URLs or prose are stored; every fact is independently re-sourced.
      </p>
      <div className="mb-6 flex gap-2">
        {["overview", "records"].map((v) => (
          <button key={v} onClick={() => setView(v)} className={`rounded-full px-4 py-1.5 text-sm font-semibold capitalize transition-colors ${view === v ? "bg-navy text-white" : "border border-border bg-white text-navy hover:bg-parchment"}`} data-testid={`archive-view-${v}`}>{v === "records" ? "Archive Records" : "Overview"}</button>
        ))}
      </div>

      {view === "records" ? (
        !records ? <p className="animate-pulse text-navy">Loading records…</p> : (
          <div data-testid="archive-records">
            <div className="mb-4 flex flex-wrap items-center gap-1.5">
              {["all", "region", "legacy_state_site", "legacy_city_site", "section_mapping", "federal_source_lead"].map((k) => (
                <button key={k} onClick={() => setKind(k)} className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${kind === k ? "bg-oldglory-red text-white" : "bg-parchment text-navy hover:bg-navy/10"}`} data-testid={`archive-kind-${k}`}>
                  {k === "all" ? `All (${Object.values(records.counts).reduce((a, b) => a + b, 0)})` : `${k.replace(/_/g, " ")} (${records.counts[k] ?? 0})`}
                </button>
              ))}
              <button onClick={exportCsv} className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-navy/20 bg-white px-3 py-1 text-xs font-semibold text-navy hover:border-oldglory-red hover:text-oldglory-red" data-testid="archive-export-csv">
                <Download className="h-3.5 w-3.5" /> Export CSV
              </button>
            </div>
            <div className="overflow-x-auto rounded-xl border border-border">
              <table className="w-full text-left text-xs">
                <thead className="bg-parchment text-[10px] uppercase tracking-wider text-navy">
                  <tr><th className="px-3 py-2">Record ID</th><th className="px-3 py-2">Kind</th><th className="px-3 py-2">Title</th><th className="px-3 py-2">Modern route</th><th className="px-3 py-2">Class</th></tr>
                </thead>
                <tbody>
                  {records.records.map((r) => (
                    <tr key={r.record_id} className="border-t border-border align-top" data-testid={`archive-record-${r.record_id}`}>
                      <td className="px-3 py-2 font-mono text-[11px] text-navy">{r.record_id}</td>
                      <td className="px-3 py-2 text-muted-foreground">{r.kind}</td>
                      <td className="px-3 py-2 text-ink">{r.legacy_title || r.legacy_section || r.state_slug || r.city_slug || "—"}</td>
                      <td className="px-3 py-2">
                        {r.modern_route && <span className="text-navy">{r.modern_route}</span>}
                        {r.source_url && <a href={r.source_url} target="_blank" rel="noopener noreferrer" className="text-oldglory-red hover:underline">source ↗</a>}
                        {r.usapulse_module && <span className="text-navy">→ {r.usapulse_module}</span>}
                        {!r.modern_route && !r.source_url && !r.usapulse_module && <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="px-3 py-2">{r.copyright_class || r.migration_status || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-[11px] text-muted-foreground">Internal reference only — this inventory is not shown publicly.</p>
          </div>
        )
      ) : !data ? (
        <p className="animate-pulse text-navy">Loading recovery inventory…</p>
      ) : (
        <div className="space-y-8">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            {[["Historical regions", data.summary.historical_regions], ["Legacy state sites", data.summary.legacy_state_sites], ["Legacy city sites", data.summary.legacy_city_sites], ["Section mappings", data.summary.section_mappings], ["Federal source leads", data.summary.federal_source_leads]].map(([l, v]) => (
              <div key={l} className="rounded-xl border border-border bg-white p-4" data-testid={`archive-stat-${l.toLowerCase().replace(/ /g, "-")}`}>
                <div className="font-display text-2xl font-black text-navy">{v}</div>
                <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{l}</div>
              </div>
            ))}
          </div>

          <section>
            <h2 className="mb-3 font-display text-xl font-black text-navy">Legacy → USAPulse section mapping</h2>
            <div className="overflow-hidden rounded-xl border border-border">
              <table className="w-full text-left text-sm">
                <thead className="bg-parchment text-[11px] uppercase tracking-wider text-navy">
                  <tr><th className="px-3 py-2">Legacy category</th><th className="px-3 py-2">USAPulse module</th><th className="px-3 py-2">Class</th></tr>
                </thead>
                <tbody>
                  {data.section_mapping.map((m, i) => (
                    <tr key={i} className="border-t border-border" data-testid={`archive-map-${i}`}>
                      <td className="px-3 py-2 text-ink">{m.legacy}</td>
                      <td className="px-3 py-2 font-medium text-navy">{m.usapulse_module || <span className="text-muted-foreground">—</span>}{m.usapulse_tab ? ` (${m.usapulse_tab})` : ""}</td>
                      <td className="px-3 py-2"><span className="rounded bg-navy/5 px-2 py-0.5 text-[11px] font-semibold text-navy" title={RECOVERY_CLASS[m.recovery_class]}>{m.recovery_class} · {RECOVERY_CLASS[m.recovery_class]}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h2 className="mb-3 font-display text-xl font-black text-navy">Historical regions &amp; coverage</h2>
            <div className="space-y-4">
              {data.historical_regions.map((r) => (
                <div key={r.key} className="rounded-xl border border-border bg-white p-4" data-testid={`archive-region-${r.key}`}>
                  <div className="mb-2 flex items-center gap-2">
                    <span className="font-display text-base font-bold text-navy">{r.label}</span>
                    <span className="rounded-full bg-navy/5 px-2 py-0.5 text-[11px] font-semibold text-navy">{r.state_count}</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {r.states.map((s) => (
                      <span key={s.geo_id} className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] ${s.in_usapulse ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-500/20" : "bg-slate-50 text-slate-500"}`} title={s.in_usapulse ? "Live on USAPulse" : "Not yet on USAPulse"}>
                        {s.in_usapulse ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}{s.geo_id}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}

function ArticleList({ user, logout, onEdit, onNew, onProgress, onSso, onModeration, onArchive, onCommunity }) {  const [articles, setArticles] = useState([]);
  const load = () => cmsList().then((d) => setArticles(d.articles)).catch(() => {});
  useEffect(() => { load(); }, []);

  const act = async (fn, id, ok) => { try { await fn(id); toast.success(ok); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); } };
  const copyDocket = async (id) => { await copyText(`${API}/docket/${id}`); toast.success("Docket API link copied"); };

  return (
    <div className="mx-auto max-w-5xl px-4 py-8" data-testid="cms-dashboard">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-black text-navy">Content Studio</h1>
          <p className="text-sm text-muted-foreground">Signed in as {user.name} · <span className="font-semibold uppercase text-oldglory-red">{user.role}</span></p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onModeration} className="inline-flex items-center gap-2 rounded-full border border-navy/20 bg-white px-4 py-2 text-sm font-semibold text-navy hover:bg-parchment" data-testid="cms-moderation-btn"><Inbox className="h-4 w-4" /> Moderation</button>
          <button onClick={onCommunity} className="inline-flex items-center gap-2 rounded-full border border-navy/20 bg-white px-4 py-2 text-sm font-semibold text-navy hover:bg-parchment" data-testid="cms-community-btn"><Users className="h-4 w-4" /> Community</button>
          <button onClick={onArchive} className="inline-flex items-center gap-2 rounded-full border border-navy/20 bg-white px-4 py-2 text-sm font-semibold text-navy hover:bg-parchment" data-testid="cms-archive-btn"><Database className="h-4 w-4" /> Archive Recovery</button>
          <button onClick={onProgress} className="inline-flex items-center gap-2 rounded-full border border-navy/20 bg-white px-4 py-2 text-sm font-semibold text-navy hover:bg-parchment" data-testid="cms-progress-btn"><BarChart3 className="h-4 w-4" /> Progress</button>
          <button onClick={onSso} className="inline-flex items-center gap-2 rounded-full border border-navy/20 bg-white px-4 py-2 text-sm font-semibold text-navy hover:bg-parchment" data-testid="cms-sso-btn"><Network className="h-4 w-4" /> Ecosystem</button>
          <button onClick={onNew} className="inline-flex items-center gap-2 rounded-full bg-oldglory-red px-4 py-2 text-sm font-semibold text-white hover:bg-oldglory-dark" data-testid="cms-new-btn"><Plus className="h-4 w-4" /> New article</button>
          <button onClick={logout} className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-semibold text-navy hover:bg-parchment" data-testid="cms-logout-btn"><LogOut className="h-4 w-4" /> Sign out</button>
        </div>
      </div>

      {articles.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-12 text-center text-muted-foreground" data-testid="cms-empty">No articles yet. Create your first one.</div>
      ) : (
        <div className="space-y-2" data-testid="cms-article-list">
          {articles.map((a) => (
            <div key={a.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-white p-4" data-testid={`cms-row-${a.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 shrink-0 text-navy" />
                  <span className="truncate font-semibold text-navy">{a.title || "Untitled"}</span>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${STATUS_STYLE[a.status] || ""}`}>{a.status.replace("_", " ")}</span>
                </div>
                <div className="mt-0.5 text-xs text-muted-foreground">{a.geo_id} · {a.module} · {a.language.toUpperCase()}</div>
              </div>
              <div className="flex items-center gap-1.5">
                <IconBtn title="Edit" onClick={() => onEdit(a.id)} testid={`cms-edit-${a.id}`}><PenLine className="h-4 w-4" /></IconBtn>
                {a.status === "draft" && <IconBtn title="Submit for review" onClick={() => act(cmsSubmit, a.id, "Submitted for review")} testid={`cms-submit-${a.id}`}><Send className="h-4 w-4" /></IconBtn>}
                {user.role === "admin" && a.status === "in_review" && (
                  <>
                    <IconBtn title="Publish" onClick={() => act(cmsPublish, a.id, "Published")} testid={`cms-publish-${a.id}`}><CheckCircle2 className="h-4 w-4 text-emerald-600" /></IconBtn>
                    <IconBtn title="Reject" onClick={() => act(cmsReject, a.id, "Sent back to draft")} testid={`cms-reject-${a.id}`}><XCircle className="h-4 w-4 text-oldglory-red" /></IconBtn>
                  </>
                )}
                {a.status === "published" && <IconBtn title="Copy docket API link" onClick={() => copyDocket(a.id)} testid={`cms-docket-${a.id}`}><Share2 className="h-4 w-4 text-navy" /></IconBtn>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CreateNew({ onCreated, onBack }) {
  const { languages } = useLanguage();
  const [tax, setTax] = useState({ modules: [] });
  const [geoId, setGeoId] = useState("");
  const [module, setModule] = useState("");
  const [language, setLanguage] = useState("en");
  useEffect(() => { getNavTaxonomy().then(setTax).catch(() => {}); }, []);

  const create = async () => {
    if (!geoId.trim() || !module) { toast.error("Enter a geo id and pick a module."); return; }
    try {
      const a = await cmsCreate({ geo_id: geoId.trim(), module, language, title: "", body_html: "" });
      onCreated(a.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create"); }
  };
  const ip = "w-full rounded-lg border border-border px-3 py-2 text-sm outline-none focus:border-navy";
  return (
    <div className="mx-auto max-w-xl px-4 py-8">
      <BackBtn onBack={onBack} />
      <h1 className="mb-4 font-display text-2xl font-black text-navy">New article</h1>
      <div className="space-y-3 rounded-xl border border-border bg-white p-5">
        <label className="block"><span className="mb-1 block text-xs font-semibold uppercase text-navy">Geo ID</span>
          <input value={geoId} onChange={(e) => setGeoId(e.target.value)} placeholder="e.g. california  or  alabama__madison-county" className={ip} data-testid="cms-geo-input" /></label>
        <label className="block"><span className="mb-1 block text-xs font-semibold uppercase text-navy">Module</span>
          <select value={module} onChange={(e) => setModule(e.target.value)} className={ip} data-testid="cms-module-select">
            <option value="">Select a module…</option>
            {tax.modules.map((m) => <option key={m.key} value={m.key}>{`${m.tab} — ${m.label}`}</option>)}
          </select></label>
        <label className="block"><span className="mb-1 block text-xs font-semibold uppercase text-navy">Language</span>
          <select value={language} onChange={(e) => setLanguage(e.target.value)} className={ip} data-testid="cms-lang-select">
            {languages.map((l) => <option key={l.code} value={l.code}>{`${l.name} — ${l.native_name}`}</option>)}
          </select></label>
        <button onClick={create} className="w-full rounded-full bg-navy py-2.5 font-semibold text-white hover:bg-navy-800" data-testid="cms-create-btn">Create draft</button>
      </div>
    </div>
  );
}

function Editor({ id, user, onBack }) {
  const [article, setArticle] = useState(null);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  const loadedRef = React.useRef(false);

  useEffect(() => {
    let cancelled = false;
    cmsGet(id).then((a) => {
      if (cancelled || loadedRef.current) return; // ignore stale/duplicate responses
      loadedRef.current = true;
      setArticle(a); setTitle(a.title || ""); setBody(a.body_html || "");
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [id]);

  const save = async (silent = false) => {
    setSaving(true);
    try { const a = await cmsUpdate(id, { title, body_html: body }); setArticle(a); if (!silent) toast.success("Saved"); return a; }
    catch { if (!silent) toast.error("Save failed"); } finally { setSaving(false); }
  };
  const submit = async () => {
    const a = await save(true);
    try { await cmsSubmit(id); toast.success("Submitted for review"); setArticle(await cmsGet(id)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not submit"); }
  };
  const publish = async () => {
    const saved = await save(true);
    try {
      if ((saved?.status || article.status) === "draft") await cmsSubmit(id);
      await cmsPublish(id);
      toast.success("Published");
      setArticle(await cmsGet(id));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not publish"); }
  };
  const reject = async () => {
    try { await cmsReject(id); toast.success("Sent back to draft"); setArticle(await cmsGet(id)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };

  if (!article) return <Center><p className="animate-pulse text-navy">Loading…</p></Center>;

  return (
    <div className="mx-auto max-w-3xl px-4 py-8" data-testid="cms-editor">
      <BackBtn onBack={onBack} />
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs text-muted-foreground">{article.geo_id} · {article.module} · {article.language.toUpperCase()} · <b className="uppercase text-oldglory-red">{article.status.replace("_", " ")}</b></span>
        {article.status === "published" && <a href={`${API}/docket/${id}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm font-semibold text-navy hover:text-oldglory-red"><Share2 className="h-4 w-4" /> Docket API</a>}
      </div>
      <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Article title" className="mb-3 w-full rounded-lg border border-border px-4 py-3 font-display text-2xl font-bold text-navy outline-none focus:border-navy" data-testid="cms-title-input" />
      <RichEditor value={body} onChange={setBody} />
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <button onClick={save} disabled={saving} className="rounded-full bg-navy px-5 py-2.5 text-sm font-semibold text-white hover:bg-navy-800 disabled:opacity-60" data-testid="cms-save-btn">{saving ? "Saving…" : "Save draft"}</button>
        {article.status !== "published" && <button onClick={submit} className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-2.5 text-sm font-semibold text-navy hover:bg-parchment" data-testid="cms-editor-submit"><Send className="h-4 w-4" /> Submit for review</button>}
        {user.role === "admin" && (
          <>
            <button onClick={publish} className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700" data-testid="cms-editor-publish"><CheckCircle2 className="h-4 w-4" /> Publish</button>
            {article.status !== "draft" && <button onClick={reject} className="inline-flex items-center gap-2 rounded-full border border-oldglory-red px-5 py-2.5 text-sm font-semibold text-oldglory-red hover:bg-oldglory-red/5" data-testid="cms-editor-reject"><XCircle className="h-4 w-4" /> Reject</button>}
          </>
        )}
      </div>
    </div>
  );
}

function ProgressDashboard({ onBack }) {
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState(false);
  const load = () => getContentStats().then(setStats).catch(() => setErr(true));
  useEffect(() => { load(); }, []);

  const pct = (done, total) => (total ? Math.round((done / total) * 100) : 0);

  return (
    <div className="mx-auto max-w-5xl px-4 py-8" data-testid="progress-dashboard">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <BackBtn onBack={onBack} />
          <h1 className="font-display text-3xl font-black text-navy">Content Progress</h1>
          <p className="text-sm text-muted-foreground">AI-generated coverage across the network — cached vs remaining.</p>
        </div>
        <button onClick={load} className="rounded-full border border-navy/20 bg-white px-4 py-2 text-sm font-semibold text-navy hover:bg-parchment" data-testid="progress-refresh-btn">Refresh</button>
      </div>

      {err && <div className="rounded-xl border border-oldglory-red/30 bg-oldglory-red/5 p-6 text-sm text-oldglory-red" data-testid="progress-error">Could not load stats. Admin access required.</div>}
      {!stats && !err && <div className="h-40 animate-pulse rounded-xl bg-parchment" data-testid="progress-loading" />}

      {stats && (
        <div className="space-y-8">
          {/* Place-article coverage bars */}
          <div className="grid gap-4 sm:grid-cols-3" data-testid="progress-coverage">
            <CoverageCard icon={MapPin} label="States / DC / Territories" done={stats.states.with_articles} total={stats.states.total} pct={pct} />
            <CoverageCard icon={Building2} label="Counties" done={stats.counties.with_articles} total={stats.counties.total} pct={pct} />
            <CoverageCard icon={MapPin} label="Cities & Towns" done={stats.cities_towns.with_articles} total={stats.cities_towns.total} pct={pct} />
          </div>

          {/* People profiles */}
          <div className="rounded-xl border border-border bg-white p-5" data-testid="progress-people">
            <div className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-navy"><Users className="h-4 w-4 text-oldglory-red" /> People Profiles</div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <MiniStat label="Total people" value={stats.people.total} />
              <MiniStat label="Profiles cached" value={stats.people.profiles_cached} accent />
              <MiniStat label="Remaining" value={stats.people.profiles_remaining} />
              <MiniStat label="Presidents" value={stats.people.presidents} />
            </div>
            <Bar done={stats.people.profiles_cached} total={stats.people.total} className="mt-4" />
          </div>

          {/* Totals */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="progress-totals">
            <TotalTile icon={Database} label="Place articles" value={stats.place_articles_cached} />
            <TotalTile icon={Sparkles} label="Person profiles" value={stats.people.profiles_cached} />
            <TotalTile icon={HelpCircle} label="FAQs published" value={stats.faqs_published} />
            <TotalTile icon={HelpCircle} label="Q&A captured" value={stats.qa_captured} />
            <TotalTile icon={Database} label="KB answers" value={stats.kb_answers} />
            <TotalTile icon={Image} label="Photos cached" value={stats.unsplash_cached} />
          </div>
        </div>
      )}
    </div>
  );
}

const SSO_STATUS_STYLE = {
  pending: "bg-slate-100 text-slate-600 border-slate-200",
  provisioned: "bg-sky-100 text-sky-700 border-sky-200",
  integrating: "bg-amber-100 text-amber-700 border-amber-200",
  live: "bg-emerald-100 text-emerald-700 border-emerald-200",
  blocked: "bg-oldglory-red/10 text-oldglory-red border-oldglory-red/30",
};

function SsoStatusBoard({ onBack }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [savingSlug, setSavingSlug] = useState(null);

  const load = () => { setErr(false); getSSOSites().then(setData).catch(() => setErr(true)); };
  useEffect(() => { load(); }, []);

  const changeStatus = async (slug, status) => {
    setSavingSlug(slug);
    try {
      await setSSOStatus(slug, status);
      setData((d) => {
        const sites = d.sites.map((s) => (s.slug === slug ? { ...s, status } : s));
        const counts = Object.fromEntries(d.statuses.map((k) => [k, 0]));
        sites.forEach((s) => { counts[s.status] = (counts[s.status] || 0) + 1; });
        return { ...d, sites, counts };
      });
      toast.success(`${slug} → ${status}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update status");
    } finally { setSavingSlug(null); }
  };

  return (
    <div className="mx-auto max-w-5xl px-4 py-8" data-testid="sso-status-board">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <BackBtn onBack={onBack} />
          <h1 className="font-display text-3xl font-black text-navy">Ecosystem SSO Status</h1>
          <p className="text-sm text-muted-foreground">SocialPulse single sign-on rollout across all sister sites.</p>
        </div>
        <button onClick={load} className="rounded-full border border-navy/20 bg-white px-4 py-2 text-sm font-semibold text-navy hover:bg-parchment" data-testid="sso-refresh-btn">Refresh</button>
      </div>

      {err && <div className="rounded-xl border border-oldglory-red/30 bg-oldglory-red/5 p-6 text-sm text-oldglory-red" data-testid="sso-error">Could not load SSO sites. Admin access required.</div>}
      {!data && !err && <div className="h-40 animate-pulse rounded-xl bg-parchment" data-testid="sso-loading" />}

      {data && (
        <div className="space-y-6">
          {/* Status summary */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="sso-summary">
            <TotalTile icon={Globe} label="Total sites" value={data.total} />
            {data.statuses.map((s) => (
              <div key={s} className={`rounded-xl border p-4 text-center ${SSO_STATUS_STYLE[s] || "border-border bg-white"}`} data-testid={`sso-count-${s}`}>
                <div className="font-display text-2xl font-black">{data.counts[s] || 0}</div>
                <div className="text-[10px] font-bold uppercase tracking-wider capitalize">{s}</div>
              </div>
            ))}
          </div>

          {/* Live progress bar */}
          <div className="rounded-xl border border-border bg-white p-5" data-testid="sso-progress">
            <div className="mb-2 flex items-center justify-between text-xs font-bold uppercase tracking-widest text-navy">
              <span className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> Sites live</span>
              <span className="text-oldglory-red">{data.counts.live || 0} / {data.total}</span>
            </div>
            <Bar done={data.counts.live || 0} total={data.total} />
          </div>

          {/* Site rows */}
          <div className="space-y-2" data-testid="sso-site-list">
            {data.sites.map((site) => (
              <div key={site.slug} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-white p-4" data-testid={`sso-row-${site.slug}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="truncate font-semibold text-navy">{site.name}</span>
                    <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase ${SSO_STATUS_STYLE[site.status] || ""}`}>{site.status}</span>
                    {site.verify_domain && <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700" title="Domain confirmation pending"><Clock className="h-3 w-3" /> verify</span>}
                  </div>
                  <a href={`https://${site.domain}`} target="_blank" rel="noopener noreferrer" className="mt-0.5 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-oldglory-red">
                    <Globe className="h-3 w-3" /> {site.domain}
                  </a>
                </div>
                <div className="flex items-center gap-2">
                  {savingSlug === site.slug && <Loader2 className="h-4 w-4 animate-spin text-navy" />}
                  <select
                    value={site.status}
                    onChange={(e) => changeStatus(site.slug, e.target.value)}
                    disabled={savingSlug === site.slug}
                    className="rounded-lg border border-border px-3 py-1.5 text-sm font-semibold text-navy outline-none focus:border-navy disabled:opacity-60"
                    data-testid={`sso-status-select-${site.slug}`}
                  >
                    {data.statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


function CoverageCard({ icon: Icon, label, done, total, pct }) {
  const p = pct(done, total);
  return (
    <div className="rounded-xl border border-border bg-white p-5">
      <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-navy"><Icon className="h-4 w-4 text-oldglory-red" /> {label}</div>
      <div className="font-display text-2xl font-black text-navy">{done.toLocaleString()} <span className="text-base font-semibold text-muted-foreground">/ {total.toLocaleString()}</span></div>
      <Bar done={done} total={total} className="mt-3" />
      <div className="mt-1.5 text-xs font-semibold text-oldglory-red">{p}% with articles · {(total - done).toLocaleString()} remaining</div>
    </div>
  );
}

function Bar({ done, total, className = "" }) {
  const p = total ? Math.min(100, Math.round((done / total) * 100)) : 0;
  return (
    <div className={`h-2.5 w-full overflow-hidden rounded-full bg-parchment ${className}`}>
      <div className="h-full rounded-full bg-gradient-to-r from-navy to-oldglory-red transition-all" style={{ width: `${p}%` }} />
    </div>
  );
}

function MiniStat({ label, value, accent }) {
  return (
    <div className={`rounded-lg border p-3 ${accent ? "border-oldglory-red/30 bg-oldglory-red/5" : "border-border bg-parchment/40"}`}>
      <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-0.5 font-display text-xl font-black text-navy">{value.toLocaleString()}</div>
    </div>
  );
}

function TotalTile({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-border bg-white p-4 text-center">
      <Icon className="mx-auto mb-1.5 h-5 w-5 text-oldglory-red" />
      <div className="font-display text-2xl font-black text-navy">{value.toLocaleString()}</div>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
    </div>
  );
}

const Center = ({ children }) => <div className="flex min-h-[70vh] items-center justify-center px-4">{children}</div>;const IconBtn = ({ title, onClick, children, testid }) => <button title={title} onClick={onClick} data-testid={testid} className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-white text-navy transition-colors hover:bg-parchment">{children}</button>;
const BackBtn = ({ onBack }) => <button onClick={onBack} className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-oldglory-red hover:text-oldglory-dark" data-testid="cms-back"><ArrowLeft className="h-4 w-4" /> Back</button>;
